import { useRef, useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import type { ProductData } from '../models/types';
import { MatchingCounter } from './MatchingCounter';

interface ToastProps {
  message: string;
  isVisible: boolean;
}

const Toast = ({ message, isVisible }: ToastProps) => (
  <div
    style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: '#333',
      color: 'white',
      padding: '10px 20px',
      borderRadius: '4px',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.3s ease',
      zIndex: 1000,
      pointerEvents: 'none',
    }}
  >
    {message}
  </div>
);

interface ProductListProps {
  filteredProducts: ProductData[];
  selectedProduct: ProductData | null;
  countTotalMatches: (productData: ProductData) => number;
  handleProductSelect: (productData: ProductData) => void;
  selectedProducts: Record<string, Record<string, string>>;
  loading: boolean;
}

export const ProductList = ({
  filteredProducts,
  selectedProduct,
  handleProductSelect,
  selectedProducts,
  loading,
}: ProductListProps) => {
  const productCardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [copiedText, setCopiedText] = useState<string>('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const handleCopy = (text: string, label: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setToastMessage(`${label} copied: ${text}`);
    setShowToast(true);
    setTimeout(() => {
      setCopiedText('');
      setShowToast(false);
    }, 2000);
  };

  const CopyableText = ({ text, label }: { text: string; label: string }) => (
    <span
      onClick={(e) => handleCopy(text, label, e)}
      className="d-inline-flex align-items-center gap-1 cursor-pointer"
      style={{ cursor: 'pointer' }}
      title={`Click to copy ${label}`}
    >
      {text}
      {copiedText === text ? (
        <span className="text-success small">✓</span>
      ) : (
        <span className="text-muted small">📋</span>
      )}
    </span>
  );

  useEffect(() => {
    if (selectedProduct && productCardRefs.current[selectedProduct.skinSeoulProduct.sku]) {
      productCardRefs.current[selectedProduct.skinSeoulProduct.sku]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedProduct]);

  const isProductComplete = (productData: ProductData): boolean => {
    const product = productData.skinSeoulProduct;
    return productData.machineMatchedProducts.every((competitor) => {
      return selectedProducts[product.sku]?.[competitor.competitor] !== undefined;
    });
  };

  const isProductInProgress = (productData: ProductData): boolean => {
    return (
      selectedProduct?.skinSeoulProduct.sku === productData.skinSeoulProduct.sku && !isProductComplete(productData)
    );
  };

  const getProductBackground = (productData: ProductData): string => {
    const isSelected = selectedProduct?.skinSeoulProduct.sku === productData.skinSeoulProduct.sku;
    const isInProgress = isProductInProgress(productData);

    if (isInProgress) {
      return '#fff8f0';
    }
    if (isSelected) {
      return '#f8f9fa';
    }
    return 'white';
  };

  return (
    <Col md={4} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        className="bg-white rounded-3 p-4 d-flex flex-column"
        style={{
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div
            className="text-uppercase"
            style={{ color: '#000000', fontSize: '16px', fontWeight: 600, letterSpacing: '1px' }}
          >
            SkinSeoul Products
          </div>
          <MatchingCounter products={filteredProducts} selectedProducts={selectedProducts} />
        </div>

        <div style={{ overflowY: 'auto', flexGrow: 1 }}>
          {filteredProducts.length > 0
            ? filteredProducts.map((productData, index) => {
                const product = productData.skinSeoulProduct;
                const isComplete = isProductComplete(productData);
                const isInProgress = isProductInProgress(productData);

                return (
                  <div key={product.id ?? product.sku} ref={(el) => (productCardRefs.current[product.sku] = el)}>
                    <button
                      className={`card mb-3 border-0 ${isInProgress ? 'shadow-lg' : selectedProduct?.skinSeoulProduct.sku === product.sku ? 'shadow' : ''}`}
                      onClick={() => handleProductSelect(productData)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleProductSelect(productData);
                          e.preventDefault();
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: getProductBackground(productData),
                        transition: 'all 0.2s ease',
                        borderRadius: '10px',
                        boxShadow: isInProgress ? '0 4px 16px rgba(255, 152, 0, 0.2)' : '0 2px 12px rgba(0,0,0,0.08)',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex gap-3">
                          <div className="position-relative" style={{ minWidth: '100px' }}>
                            {product.productImageUrls && product.productImageUrls.length > 0 ? (
                              <img
                                src={product.productImageUrls[0]}
                                alt={product.name}
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  backgroundColor: '#f8f9fa',
                                }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  borderRadius: '8px',
                                  backgroundColor: '#f8f9fa',
                                }}
                              >
                                No Image
                              </div>
                            )}
                            <div
                              className="position-absolute top-0 start-0 rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '24px',
                                height: '24px',
                                fontSize: '12px',
                                transform: 'translate(-30%, -30%)',
                                backgroundColor: '#3d1690',
                                color: 'white',
                              }}
                            >
                              {index + 1}
                            </div>
                            {isComplete && (
                              <div
                                className="position-absolute bottom-0 end-0 rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  fontSize: '14px',
                                  transform: 'translate(30%, 30%)',
                                }}
                              >
                                ✓
                              </div>
                            )}
                            {isInProgress && (
                              <div
                                className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  fontSize: '14px',
                                  transform: 'translate(30%, 30%)',
                                  backgroundColor: '#ff9800',
                                  color: 'white',
                                }}
                              >
                                ⟳
                              </div>
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex flex-column gap-2 mt-2">
                              <div className="d-flex gap-3">
                                <div className="small text-muted">
                                  SKU: <CopyableText text={product.sku} label="SKU" />
                                </div>
                                <div className="small text-muted">
                                  Brand: <CopyableText text={product.brand} label="Brand" />
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6
                                  className="mb-0"
                                  style={{
                                    fontSize: '1rem',
                                    lineHeight: '1.4',
                                    color: isInProgress ? '#ff9800' : '#000',
                                    fontWeight: isInProgress ? 600 : 400,
                                  }}
                                >
                                  <CopyableText text={product.name} label="Product name" />
                                </h6>
                              </div>

                              <div className="d-flex gap-2 flex-wrap">
                                {product.category.map((cat) => (
                                  <span
                                    key={`${product.sku}-${cat}`}
                                    className="badge bg-light text-dark small"
                                    style={{ backgroundColor: '#f0f0f0' }}
                                  >
                                    {cat}
                                  </span>
                                ))}
                              </div>

                              {product.type && (
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    className={`badge bg-${product.type === 'Simple' ? 'success' : 'warning'} small`}
                                    style={{ fontSize: '0.7rem' }}
                                  >
                                    {product.type}
                                  </span>
                                  {isInProgress && (
                                    <span className="badge bg-warning text-dark small" style={{ fontSize: '0.7rem' }}>
                                      Matching in progress
                                    </span>
                                  )}
                                </div>
                              )}

                              {(product.originalPrice || product.salePrice) && (
                                <div className="d-flex gap-2 align-items-center">
                                  {product.salePrice && (
                                    <span className="fw-bold" style={{ color: '#3d1690' }}>
                                      {product.currency} {product.salePrice.toFixed(2)}
                                    </span>
                                  )}
                                  {product.originalPrice &&
                                    product.salePrice &&
                                    product.originalPrice > product.salePrice && (
                                      <span className="text-muted text-decoration-line-through small">
                                        {product.currency} {product.originalPrice.toFixed(2)}
                                      </span>
                                    )}
                                </div>
                              )}

                              {product.productLink && (
                                <div className="small mt-1">
                                  <a
                                    href={product.productLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-primary"
                                  >
                                    View in SkinSeoul
                                  </a>
                                </div>
                              )}

                              {product.modifiedAt && (
                                <div className="small text-muted">
                                  Last modified: {new Date(product.modifiedAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })
            : !loading && <div className="text-center text-muted py-4">No products found</div>}
        </div>
      </div>
      <Toast message={toastMessage} isVisible={showToast} />
    </Col>
  );
};
